/**
 * @fileoverview Session History Sidebar Component
 *
 * Displays past flashcard review sessions with:
 * - Session duration and card count
 * - Grade distribution visualization
 * - Average confidence rating
 *
 * @module components/SessionHistorySidebar
 */

import { useQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'
import { Clock, BookOpen, TrendingUp, X } from 'lucide-react'

interface SessionHistorySidebarProps {
  userId: Id<'users'>
  isOpen: boolean
  onClose: () => void
}

export function SessionHistorySidebar({ userId, isOpen, onClose }: SessionHistorySidebarProps) {
  const sessions = useQuery(api.flashcards.getRecentSessions, {
    userId,
    limit: 10,
  })

  if (!isOpen) return null

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins === 0) return `${secs}s`
    return `${mins}m ${secs}s`
  }

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))

    // Show relative time for recent sessions
    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffHours < 48) return 'Yesterday'

    // Show formatted date for older sessions
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    })
  }

  const getGradeColor = (grade: number) => {
    if (grade <= 1) return 'bg-red-600'
    if (grade === 2) return 'bg-orange-500'
    if (grade === 3) return 'bg-yellow-500'
    if (grade === 4) return 'bg-lime-500'
    return 'bg-terminal-green'
  }

  const getAverageGradeLabel = (avg: number) => {
    if (avg < 2) return 'Needs Practice'
    if (avg < 3) return 'Learning'
    if (avg < 4) return 'Good Progress'
    if (avg < 4.5) return 'Strong Recall'
    return 'Mastered'
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
        onClick={onClose}
        aria-label="Close sidebar"
      />

      {/* Sidebar */}
      <div className="fixed top-0 right-0 h-full w-full sm:w-96 bg-black border-l-2 border-sky-blue overflow-y-auto z-50 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-black border-b-2 border-sky-blue p-4 flex items-center justify-between">
          <h2 className="text-lg font-bold font-mono text-sky-blue tracking-wider">
            [SESSION HISTORY]
          </h2>
          <button
            onClick={onClose}
            className="text-trail-tan hover:text-terminal-green transition-colors"
            aria-label="Close sidebar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {!sessions || sessions.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-trail-tan font-mono text-sm">
                No review sessions yet
              </p>
              <p className="text-gray-500 font-mono text-xs mt-2">
                Complete a flashcard review to see your progress
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <div
                  key={session._id}
                  className="terminal-border border-sky-blue p-4 bg-gray-900/50 hover:bg-gray-900/80 transition-colors"
                >
                  {/* Session header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="text-sky-blue font-mono text-sm font-bold mb-1">
                        {formatDate(session.completedAt)}
                      </div>
                      <div className="flex items-center gap-4 text-xs font-mono">
                        <span className="flex items-center gap-1 text-trail-tan">
                          <Clock className="w-3 h-3" />
                          {formatDuration(session.duration)}
                        </span>
                        <span className="flex items-center gap-1 text-trail-tan">
                          <BookOpen className="w-3 h-3" />
                          {session.cardsReviewed} cards
                        </span>
                      </div>
                    </div>

                    {/* Average grade badge */}
                    <div className="flex flex-col items-end">
                      <div className="flex items-center gap-1 mb-1">
                        <TrendingUp className="w-3 h-3 text-terminal-green" />
                        <span className="text-terminal-green font-mono text-lg font-bold">
                          {session.averageGrade.toFixed(1)}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-gray-400">
                        {getAverageGradeLabel(session.averageGrade)}
                      </span>
                    </div>
                  </div>

                  {/* Grade distribution visualization */}
                  <div className="mt-3">
                    <div className="text-xs font-mono text-gray-400 mb-2">
                      [CONFIDENCE DISTRIBUTION]
                    </div>
                    <div className="flex gap-1 h-16">
                      {[0, 1, 2, 3, 4, 5].map((grade) => {
                        const count = session.grades.filter((g) => g === grade).length
                        const percentage = (count / session.grades.length) * 100

                        return (
                          <div key={grade} className="flex-1 flex flex-col justify-end">
                            <div
                              className={`${getGradeColor(grade)} transition-all duration-300 min-h-[2px]`}
                              style={{ height: `${percentage}%` }}
                              title={`Grade ${grade}: ${count} cards (${percentage.toFixed(0)}%)`}
                            />
                            <div className="text-xs text-center text-gray-500 font-mono mt-1">
                              {grade}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="mt-3 pt-3 border-t border-gray-700/50 flex items-center justify-between text-xs font-mono">
                    <span className="text-gray-500">0-1: Hard</span>
                    <span className="text-gray-500">2-3: Medium</span>
                    <span className="text-gray-500">4-5: Easy</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
